import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Inbox, Phone, MessageSquare, AlertTriangle, Clock, ChevronDown, ChevronUp,
  Loader2, MapPin, Briefcase, RefreshCw, X, CheckCircle2, PhoneOff, CalendarCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'urgent', label: 'Urgent' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
];

const URGENCY_BADGE = {
  high: { label: 'URGENT', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  medium: { label: 'Medium', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  low: { label: 'Low', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
};

const STATUS_BADGE = {
  new: { label: 'New', cls: 'bg-primary/15 text-primary border-primary/30' },
  contacted: { label: 'Contacted', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  no_answer: { label: 'No answer', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  booked: { label: 'Booked', cls: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
};

const STATUS_ACTIONS = [
  { id: 'contacted', label: 'Contacted', icon: CheckCircle2 },
  { id: 'no_answer', label: 'No answer', icon: PhoneOff },
  { id: 'booked', label: 'Booked', icon: CalendarCheck },
];

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
    if (diffMin < 60 * 24 * 7) return `${Math.floor(diffMin / 60 / 24)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso.slice(0, 10);
  }
}function UrgencyBadge({ level }) {
  const cfg = URGENCY_BADGE[level];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.cls}`}
      data-testid={`urgency-badge-${level}`}
    >
      {level === 'high' && <AlertTriangle size={10} />}
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.new;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cfg.cls}`}
      data-testid={`status-badge-${status || 'new'}`}
    >
      {cfg.label}
    </span>
  );
}

function IndustryBadge({ industry, name }) {
  if (!industry) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
      data-testid={`industry-badge-${industry}`}
    >
      <Briefcase size={10} />
      {name || industry.replace(/_/g, ' ')}
    </span>
  );
}

function fieldByLabel(fields, label) {
  if (!Array.isArray(fields)) return '';
  const f = fields.find((x) => x.label === label);
  return f && f.value && f.value !== 'Not provided' ? f.value : '';
}

function projectTypeOf(lead) {
  // Try common labels across industries
  return (
    fieldByLabel(lead.fields, 'Project type') ||
    fieldByLabel(lead.fields, 'Issue type') ||
    fieldByLabel(lead.fields, 'Service type') ||
    fieldByLabel(lead.fields, 'System type') ||
    ''
  );
}

function addressOf(lead) {
  return (
    fieldByLabel(lead.fields, 'Project address') ||
    fieldByLabel(lead.fields, 'Property address') ||
    ''
  );
}

function summaryShort(text) {
  if (!text) return '';
  if (text.length <= 220) return text;
  return text.slice(0, 217).trimEnd() + '…';
}

function LeadCard({ lead, expanded, onToggle, onUpdateStatus, busy }) {
  const phone = lead.customer_phone || '';
  const address = addressOf(lead);
  const project = projectTypeOf(lead);
  const status = lead.status || 'new';
  const isHandled = status !== 'new';

  return (
    <div
      className={`rounded-xl border border-border bg-card p-4 sm:p-5 transition-colors hover:border-muted-foreground/30 ${isHandled ? 'opacity-70' : ''}`}
      data-testid={`lead-card-${lead.call_log_id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <h3
            className="text-base font-semibold text-foreground truncate"
            data-testid={`lead-name-${lead.call_log_id}`}
          >
            {lead.customer_name || 'Unknown caller'}
          </h3>
          {phone && (
            <p
              className="text-xs text-muted-foreground mt-0.5 font-mono"
              data-testid={`lead-phone-${lead.call_log_id}`}
            >
              {phone}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <div className="flex items-center gap-1.5">
            <StatusBadge status={status} />
            <UrgencyBadge level={lead.urgency} />
          </div>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Clock size={10} /> {fmtTime(lead.created_at)}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <IndustryBadge industry={lead.industry} name={lead.industry_name} />
        {project && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-muted text-foreground/80 border border-border">
            {project}
          </span>
        )}
      </div>

      {address && (
        <p className="text-xs text-muted-foreground flex items-start gap-1.5 mb-2">
          <MapPin size={12} className="mt-0.5 shrink-0" />
          <span className="break-words">{address}</span>
        </p>
      )}

      {lead.ai_summary && (
        <p className="text-sm text-foreground/80 leading-relaxed mb-3">
          {expanded ? lead.ai_summary : summaryShort(lead.ai_summary)}
        </p>
      )}

      {expanded && Array.isArray(lead.fields) && lead.fields.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border" data-testid={`lead-detail-${lead.call_log_id}`}>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Captured Fields
          </h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {lead.fields.map((f) => (
              <div key={f.key || f.label} className="flex flex-col">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd
                  className={f.value === 'Not provided' ? 'text-muted-foreground/60 italic' : 'text-foreground'}
                >
                  {f.value}
                </dd>
              </div>
            ))}
          </dl>
          {lead.status_updated_at && (
            <p className="text-[11px] text-muted-foreground mt-3" data-testid={`lead-status-updated-${lead.call_log_id}`}>
              Status updated {fmtTime(lead.status_updated_at)}
            </p>
          )}
        </div>
      )}

      {/* Mark as actions */}
      <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`lead-mark-as-${lead.call_log_id}`}>
        <span className="text-[11px] text-muted-foreground mr-1">Mark as:</span>
        {STATUS_ACTIONS.map(({ id, label, icon: Icon }) => {
          const active = status === id;
          return (
            <button
              key={id}
              type="button"
              disabled={busy}
              onClick={() => onUpdateStatus(active ? 'new' : id)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                active
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : 'bg-muted text-foreground/70 border-border hover:bg-accent'
              } ${busy ? 'opacity-50 cursor-wait' : ''}`}
              data-testid={`lead-mark-${id}-${lead.call_log_id}`}
            >
              <Icon size={11} /> {label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {phone && (
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 text-xs font-medium hover:bg-emerald-500/20 transition-colors"
            data-testid={`lead-call-${lead.call_log_id}`}
          >
            <Phone size={13} /> Call
          </a>
        )}
        {phone && (
          <a
            href={`sms:${phone}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-medium hover:bg-sky-500/20 transition-colors"
            data-testid={`lead-text-${lead.call_log_id}`}
          >
            <MessageSquare size={13} /> Text
          </a>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-foreground/80 text-xs font-medium hover:bg-accent/70 transition-colors ml-auto"
          data-testid={`lead-toggle-${lead.call_log_id}`}
        >
          {expanded ? (
            <>
              <ChevronUp size={13} /> Hide details
            </>
          ) : (
            <>
              <ChevronDown size={13} /> View details
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default function LeadInboxPage() {
  const { token } = useAuth();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, today: 0, week: 0, urgent: 0, new: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filter === 'urgent') params.urgency = 'high';
      else if (filter === 'today') params.window = 'today';
      else if (filter === 'week') params.window = 'week';
      else if (filter === 'new') params.status = 'new';
      else if (filter === 'contacted') params.status = 'contacted';

      const { data } = await axios.get(`${API}/lead-summaries/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setCounts(data?.counts || { all: 0, today: 0, week: 0, urgent: 0, new: 0, contacted: 0 });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load lead inbox');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateStatus = async (callLogId, newStatus) => {
    setBusyId(callLogId);
    // Optimistic update
    setItems((prev) =>
      prev.map((it) =>
        it.call_log_id === callLogId
          ? { ...it, status: newStatus, status_updated_at: new Date().toISOString() }
          : it
      )
    );
    try {
      await axios.patch(
        `${API}/lead-summaries/${callLogId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh counts (and if a status filter is active, may drop the card)
      fetchLeads();
    } catch (e) {
      // Revert on failure
      setError(e?.response?.data?.detail || 'Failed to update status');
      fetchLeads();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="lead-inbox-page">
      <header className="mb-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Inbox size={18} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Lead Inbox</h1>
              <p className="text-xs text-muted-foreground">
                AI-generated lead summaries from completed calls
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLeads}
            disabled={loading}
            data-testid="refresh-leads-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline ml-1.5">Refresh</span>
          </Button>
        </div>
      </header>

      {/* Filter tabs */}
      <div
        className="flex flex-wrap items-center gap-2 mb-5 overflow-x-auto -mx-1 px-1"
        data-testid="lead-filter-tabs"
      >
        {FILTERS.map((f) => {
          const isActive = filter === f.id;
          let count;
          if (f.id === 'all') count = counts.all;
          else if (f.id === 'urgent') count = counts.urgent;
          else if (f.id === 'today') count = counts.today;
          else if (f.id === 'week') count = counts.week;
          else if (f.id === 'new') count = counts.new;
          else if (f.id === 'contacted') count = counts.contacted;

          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors border ${
                isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-foreground/80 border-border hover:border-muted-foreground/30'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
              {typeof count === 'number' && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-primary-foreground/20' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading && items.length === 0 && (
        <div className="flex items-center justify-center py-20" data-testid="leads-loading">
          <Loader2 className="animate-spin text-muted-foreground" size={28} />
        </div>
      )}

      {!loading && error && (
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive p-4 text-sm flex items-start gap-2"
          data-testid="leads-error"
        >
          <X size={16} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div
          className="rounded-xl border border-border bg-card p-10 text-center"
          data-testid="leads-empty"
        >
          <Inbox size={28} className="mx-auto text-muted-foreground mb-3" />
          <h3 className="text-base font-semibold text-foreground mb-1">No leads yet</h3>
          <p className="text-sm text-muted-foreground">
            AI-generated lead summaries from your trade-industry calls will appear here.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-3" data-testid="leads-list">
          {items.map((lead) => (
            <LeadCard
              key={lead.call_log_id}
              lead={lead}
              expanded={expandedId === lead.call_log_id}
              busy={busyId === lead.call_log_id}
              onToggle={() =>
                setExpandedId(expandedId === lead.call_log_id ? null : lead.call_log_id)
              }
              onUpdateStatus={(newStatus) => updateStatus(lead.call_log_id, newStatus)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
